import { AlertCircle, Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import axiosWithTenant from '../utils/axiosHandler';

const SquarePaymentForm = ({ amount, onSuccess, onError, buttonText = "Pay Now" }) => {
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [squareConfig, setSquareConfig] = useState(null);
    const cardRef = useRef(null);
    const paymentFormRef = useRef(null);

    useEffect(() => {
        // Load Square configuration
        const loadSquareConfig = async () => {
            try {
                setError(''); // Clear any previous errors
                //TODO: remove this hardcoded API URL later
                // Hardcoded API URL based on hostname
                const hostname = window.location.hostname;
                let API_URL;

                // Production domains
                if (hostname === 'lima.ac' || hostname.includes('lima.ac')) {
                    API_URL = 'https://lima.ac/api';
                } else if (hostname === 'lima.pitech.ca' || hostname.includes('lima.pitech.ca')) {
                    API_URL = 'https://lima.pitech.ca/api';
                } else if (hostname === 'bagbox.ca' || hostname.includes('bagbox.ca')) {
                    API_URL = 'https://bagbox.ca/api';
                } else {
                    // Development/localhost
                    API_URL = 'http://localhost:80/api';
                }

                const response = await axiosWithTenant.get(`${API_URL}/subscriptions/square-config`);
                setSquareConfig(response.data);
            } catch (err) {
                console.error('Error loading Square config:', err);
                setError('Failed to load payment configuration');
                setLoading(false);
            }
        };

        loadSquareConfig();
    }, []);

    useEffect(() => {
        if (!squareConfig) return;

        // Load Square Web Payments SDK
        const script = document.createElement('script');

        // Use the appropriate SDK URL based on environment
        if (squareConfig.environment === 'sandbox') {
            script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
        } else {
            script.src = 'https://web.squarecdn.com/v1/square.js';
        }

        script.async = true;
        script.onload = () => {
            // Add a small delay to ensure DOM is ready
            setTimeout(initializeSquare, 100);
        };
        script.onerror = () => {
            setError('Failed to load payment system');
            setLoading(false);
        };
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [squareConfig]);

    const initializeSquare = async () => {
        if (!window.Square) {
            setError('Payment system not available');
            setLoading(false);
            return;
        }

        try {
            // Clear any previous errors
            setError('');

            // Ensure the container element exists
            const container = document.getElementById('card-container');
            if (!container) {
                console.error('Card container element not found');
                // Wait a bit and try again
                await new Promise(resolve => setTimeout(resolve, 200));
                const retryContainer = document.getElementById('card-container');
                if (!retryContainer) {
                    setError('Payment form container not ready');
                    setLoading(false);
                    return;
                }
            }

            console.log('Initializing Square with:', {
                applicationId: squareConfig.applicationId,
                locationId: squareConfig.locationId,
                environment: squareConfig.environment
            });

            // Initialize Square Payments with environment configuration
            const payments = window.Square.payments(
                squareConfig.applicationId,
                squareConfig.locationId
            );

            // Configure card options based on environment
            const cardOptions = {};

            const card = await payments.card(cardOptions);
            await card.attach('#card-container');

            paymentFormRef.current = { card, payments };
            setError(''); // Clear any errors
            setLoading(false);
            console.log('Square payment form initialized successfully');
        } catch (err) {
            console.error('Square initialization error:', err);
            setError(`Failed to initialize payment form: ${err.message || 'Unknown error'}`);
            setLoading(false);
        }
    };

    const handlePayment = async () => {
        if (!paymentFormRef.current) {
            setError('Payment form not ready');
            return;
        }

        setProcessing(true);
        setError('');

        try {
            const { card } = paymentFormRef.current;

            // Tokenize the card
            const tokenResult = await card.tokenize();

            if (tokenResult.status === 'OK') {
                // Payment successful, return the token
                if (onSuccess) {
                    await onSuccess({
                        sourceId: tokenResult.token,
                        verificationToken: tokenResult.token
                    });
                }
            } else {
                // Handle tokenization errors
                let errorMessage = 'Payment failed';

                if (tokenResult.errors && tokenResult.errors.length > 0) {
                    errorMessage = tokenResult.errors.map(error => error.message).join(', ');
                }

                setError(errorMessage);
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            console.error('Payment error:', err);
            const errorMessage = err.message || 'Payment processing failed';
            setError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-600">Loading payment form...</span>
            </div>
        );
    }

    return (
        <div>
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-red-900">Error</p>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
            )}

            <div id="card-container" className="mb-4 p-4 border border-gray-300 rounded-lg bg-white"></div>

            <button
                onClick={handlePayment}
                disabled={processing || loading}
                className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
                {processing ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                    </>
                ) : (
                    buttonText
                )}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
                Your payment is processed securely through Square. We don't store your card details.
            </p>
        </div>
    );
};

export default SquarePaymentForm;

